//* react
import { isNative } from 'platform/native';
import React from 'react';

//* material-ui
import Grid from '@mui/material/Grid2';
import { Box, Link } from '@mui/material';

//* types
import { ChatUserModel } from 'types/chat-users/chat-user';
import {
  ConversationOpenStatusSocketType,
  LabelUpdateSocketType,
  ReceiveMessageSocketType,
  ReceiveTemplateMessageSocketType,
  SocketEventType,
  SocketMessageData,
  UpdateChatUserDontDisturbSocketType,
  UpdateChatUserUnreadSocketType,
  UpdateCompanyPlatformsSocketType,
  UpdateMessageSocketType,
  SessionUpdateSocketType,
  ConversationTagsSocketType,
} from 'types/socket';
import { normalizeChatMessageFlow } from 'types/chat/messages/common';
import { MessageModel } from 'types/chat/messages/message';

//* system
import { dispatch, store } from 'store';
import { getUserConfig } from 'store/slices/userConfig';
import { customViewHasUnsupportedConditions, doesUserMatchCurrentFilters, getMessageStatus } from 'utils/chat';
import {
  relocateChatUserInViewLists,
  saveSingleMessageToUser,
  scheduleCustomViewRefetch,
  setChatUsers,
  setSelectedChatUser,
  updateSingleChatUser,
  updateSingleChatUserByPhone,
} from 'store/slices/chat';
import { logChatReply, payloadLooksLikeReplyMetadata } from 'utils/debug-chat-reply';
import { extractReplyPayloadWithWeb3Fallback } from 'utils/extract-reply-from-api';
import {
  getMessageBodyFromPayload,
  getMessageMediaTypeFromPayload,
  getMessageNumericIdFromPayload,
  getWhatsappMessageIdFromPayload,
} from 'utils/normalize-chat-message-payload';
import {
  extractReactionEmojiFromPayload,
  isReactionSocketPayload,
  mergeReactionEmoji,
  resolveReactionTargetMessageId,
} from 'utils/normalize-message-reactions';
import { resolveMessageNumericId } from 'utils/reply-message-id';
import { getBrowserNotifStatus, handleBrowserNotification, safeParseJSON } from 'utils/helpers';
import { getUnreadChatUsers } from 'store/slices/unread-chat-users';
import { scheduleUnreadCountsRefresh } from 'utils/unread-counts-refresh';
import { pushSystemNotification } from 'store/slices/system-notifications';
import { findChatUserByPhoneInStore } from 'utils/find-chat-user-in-store';
import { normalizeSnoozeEndTimeFromApi } from 'utils/snooze-datetime';
import { setPersistedSnoozeUntil } from 'utils/chat-workflow-overrides';
import {
  shouldDeliverGeneralNotification,
  shouldDeliverTaggedNoteNotification,
} from 'utils/notification-preferences';
import { resolveStatusHebFromServerStatus } from 'utils/whatsappErrors';
// import { enqueueSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
import useAuth from 'hooks/useAuth';
import { updateSingleApplicationUser } from 'store/slices/applicationUsers';
import { CompanyPlatformsModel } from 'types/company-platforms/companyPlatforms';
import { updateCompanyPlatformsData } from 'store/slices/companyPlatforms';
import {
  isForeignChatUserCompany,
  repairChatUserCompanyIdIfEmpty,
  resolveStoreCompanyId,
} from 'utils/resolve-chat-user-company-id';
import { noteMessageOutcome, recordSocketHealth } from 'utils/socket-health-monitor';
import { useSocketListeners } from './useSocketListeners';
import { useSnackbar } from 'notistack';
import NotificationIcon from 'ui-component/third-party/NotificationIcon';
import { ChatViewUsersId, getCurrentView } from './useCurrentChatView';
import {
  mapSocketPayloadToMessageModel,
  unwrapSocketMessageEnvelope,
} from 'utils/normalize-socket-incoming-message';
import { getChatUserByPhoneNumber } from 'api/chat-users';
import { fetchChatUserByPhoneWeb3 } from 'api/chat';

/**
 * Hook for handling global socket events
 */

function normalizeSocketManagerId(raw: unknown): string | null {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'string') return raw;
  return String(raw);
}

function buildAssigneePatchFromManagerId(
  managerId: string | null,
): Partial<ChatUserModel> {
  if (!managerId) {
    return { NotificationContactUser: null, NotificationContactName: null };
  }
  const appUser = store.getState().applicationUsers.find((u) => u.Udid === managerId);
  return {
    NotificationContactUser: managerId,
    NotificationContactName:
      appUser?.FullName ??
      (appUser ? `${appUser.FirstName || ''} ${appUser.LastName || ''}`.trim() : null),
  };
}

//* Cooldown for fetch-recovery of contacts missing from the loaded lists, so a burst
//* of messages from the same unknown contact triggers a single request.
const unknownContactRecoveryAt = new Map<string, number>();
const UNKNOWN_CONTACT_RECOVERY_COOLDOWN_MS = 15_000;

const socketEvents: Array<SocketEventType> = [
  'ReceiveMessage',
  'message:new',
  'session:update',
  'conversation:tags',
  'MessageStatusUpdate',
  'status:update',
  'ReceiveTemplateMessage',
  'unreadNotification',
  'UnreadUpdate',
  'updateChatUserUnread',
  'Notification',
  'TaggedUsersNotification',
  'update:new',
  'userConfig:views',
  'userConfig:permissions',
  'labelUpdate',
  'conversationOpenStatus',
  'funnerServiceNotification',
  'UpdateChatUserDontDisturb',
  'updateFunnerUserActive',
  'UpdateCompanyPlatforms',
  'ContactBotStatusChange',
  'Logout',
];

//* ===== FIX #2 (2026-07-13) — Rules of Hooks sentinel =====
//* Stable, module-level empty array used when the user is not yet authenticated,
//* so useSocketListeners registers NO handlers WITHOUT us having to early-return
//* before the hooks below. Kept module-level (single stable reference) so it never
//* causes needless off/on churn inside useSocketListeners' effect deps.
const NO_SOCKET_EVENTS: Array<SocketEventType> = [];

export default function useGlobalSocketEvents() {
  const { logout, isInitialized, isLoggedIn } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();

  //* ===== FIX #2 (2026-07-13) — Rules of Hooks violation =====
  //* PROBLEM: this hook USED TO early-return right here:
  //*     if (!isInitialized || !isLoggedIn) return;
  //*   ...which sat ABOVE every `React.useCallback` below and the `useSocketListeners`
  //*   call at the very bottom. When auth state flips mid-session (Clerk token
  //*   refresh, auth re-evaluation, logout/login) React renders a DIFFERENT number
  //*   of hooks between renders. Best case it throws "rendered fewer hooks than
  //*   expected"; in practice the render that returned early ran useSocketListeners'
  //*   cleanup (`connection.off` for every event) and then never re-registered —
  //*   silently detaching the incoming-message listeners until a full page refresh.
  //*
  //* FIX: the early return is REMOVED. All hooks now run unconditionally on every
  //*   render (stable hook order). Registration is instead gated at the bottom of
  //*   this hook by swapping the events array (socketEvents vs NO_SOCKET_EVENTS).
  //*
  //* TO REVERT: restore `if (!isInitialized || !isLoggedIn) return;` right here and
  //*   change the final call back to `useSocketListeners(socketEvents, onSocketNotif);`
  //*   (and optionally delete NO_SOCKET_EVENTS above).

  //* "Receive message"
  const insertUsersAccordingToFilters = React.useCallback(function (
    currentUser: ChatUserModel,
    newUsersList: Array<ChatUserModel>,
    view: ChatViewUsersId,
  ) {
    const doesUserMatchFilters = doesUserMatchCurrentFilters(currentUser, view);

    //* תצוגה מותאמת עם תנאי שהלקוח לא יודע להעריך: "תואם" הוא ניחוש. שורה שכבר הייתה
    //* ברשימה (ה-store עדיין מחזיק את הרשימה שלפני האירוע) מוחזרת כרגיל; שורה חדשה לא
    //* נכנסת — במקום זה נמשך העמוד הראשון מחדש (חקירת 4.11: כל הודעה נכנסת בחברה
    //* הצטרפה לתצוגה "תגית איש קשר = X" והרשימה גדלה בעוד התג מהשרת לא).
    if (doesUserMatchFilters && view === 'customChatUsers') {
      const chatState = store.getState().chat;
      const wasInList = chatState.chatUsers.customChatUsers.some(
        (u) => u.UserDisplayPhoneNumber === currentUser.UserDisplayPhoneNumber,
      );
      if (!wasInList && customViewHasUnsupportedConditions(chatState.lastRequestedCustomViewId)) {
        scheduleCustomViewRefetch(chatState.lastRequestedCustomViewId!, 'socket-insert');
        return newUsersList;
      }
    }

    if (doesUserMatchFilters) {
      if (newUsersList.length === 0) {
        updateSingleChatUser(currentUser.Id, { IsOpen: true });
      }
      newUsersList.unshift(currentUser);
    }

    return newUsersList;
  }, []);
  const receiveMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, newChatUser: ChatUserModel, newMessage: MessageModel) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      
      if (!areUsersRequested[`${view}`]) {
        console.log(`[DEBUG Socket] ${view} - returning [] because areUsersRequested is false`);
        return [];
      }

      const chatUsers = storeState.chat.chatUsers[`${view}`];
      const targetUser = chatUsers.find(
        (user) => user.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber,
      );


      const isSameMessage =
        targetUser?.LastMessageType === newChatUser?.LastMessageType &&
        (newChatUser?.LastMessageType === 'outcoming'
          ? targetUser?.OutcomingLastMessageId === newChatUser?.OutcomingLastMessageId
          : targetUser?.incomingLastMessageId === newChatUser?.incomingLastMessageId);

      let newUsersList: Array<ChatUserModel> = [];
      if (isSameMessage) {
        newUsersList = chatUsers.map((user) => {
          if (user.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
            return newChatUser;
          }

          return user;
        });
      } else {
        newUsersList = chatUsers.filter(
          (user) => user.UserDisplayPhoneNumber !== newChatUser.UserDisplayPhoneNumber,
        );
        

        //* Relate inserting user with active filters
        newUsersList = insertUsersAccordingToFilters(newChatUser, newUsersList, view);
      }

      console.log(`[DEBUG Socket] ${view} - chatUsers count AFTER:`, newUsersList.length);
      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  //* A message arrived for a contact that is missing from the loaded lists — either a
  //* brand-new contact or one that fell out of the local copy. Without recovery every
  //* message from it is silently dropped (no list row, no unread) until a manual refresh.
  const recoverUnknownChatUser = React.useCallback(
    async (phone: string) => {
      const now = Date.now();
      const lastAttempt = unknownContactRecoveryAt.get(phone) ?? 0;
      if (now - lastAttempt < UNKNOWN_CONTACT_RECOVERY_COOLDOWN_MS) return;
      unknownContactRecoveryAt.set(phone, now);

      try {
        //* מסלול ראשי: הנוד (getContacts) — שורה מלאה כולל SessionId ותגיות שיחה;
        //* legacy getLegasyByPhone נשאר כגיבוי בלבד (בלי השדות האלה)
        let recovered = await fetchChatUserByPhoneWeb3(phone);
        if (!recovered) {
          const res = await getChatUserByPhoneNumber(phone);
          recovered = res?.contact?.[0] ?? null;
        }
        if (!recovered) return;
        const contact = recovered;

        //* Re-check: a parallel event may have inserted the contact meanwhile.
        if (findChatUserByPhoneInStore(phone)) return;

        const storeState = store.getState();
        const { chatUsers, areUsersRequested } = storeState.chat;

        const newChatUsers = (Object.keys(chatUsers) as Array<keyof typeof chatUsers>).reduce(
          (acc, key) => {
            if (key === 'loading' || !areUsersRequested[`${key}` as keyof typeof areUsersRequested]) {
              acc[key] = chatUsers[key] as never;
            } else {
              const withoutContact = (chatUsers[key] as ChatUserModel[]).filter(
                (user) => user.UserDisplayPhoneNumber !== contact.UserDisplayPhoneNumber,
              );
              acc[key] = insertUsersAccordingToFilters(
                contact,
                withoutContact,
                key as ChatViewUsersId,
              ) as never;
            }
            return acc;
          },
          {} as typeof chatUsers,
        );

        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
            completedChatUsers: newChatUsers.completedChatUsers,
          }),
        );

        getUnreadChatUsers();
        scheduleUnreadCountsRefresh('recover-unknown-contact');
      } catch (error) {
        console.warn('recoverUnknownChatUser error: ', error);
      }
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveMessage = React.useCallback(
    function (messageObject: ReceiveMessageSocketType) {
      try {
        const unwrapped = unwrapSocketMessageEnvelope(messageObject);
        if (!unwrapped?.toUser) return;

        const { messageData: md, toUser: toUserNorm } = unwrapped;

        const storeState = store.getState();
        const companyId = resolveStoreCompanyId();
        if (!companyId) return;

        const { chatUsers, selectedChatUser } = storeState.chat;

        const allChatUsers = [
          ...chatUsers.myChatUsers,
          ...chatUsers.mySnoozedChatUsers,
          ...chatUsers.notMyChatUsers,
          ...chatUsers.unassignedChatUsers,
          ...chatUsers.botsChatUsers,
          ...chatUsers.allChatUsers,
          ...chatUsers.customChatUsers,
          ...chatUsers.completedChatUsers,
        ];

        const sessionIdRaw = md.sessionId ?? md.SessionId;
        let chatUser =
          sessionIdRaw && typeof sessionIdRaw === 'string'
            ? allChatUsers.find((u) => u.SessionId === sessionIdRaw)
            : null;

        if (!chatUser) {
          chatUser = allChatUsers.find((u) => u.UserDisplayPhoneNumber === toUserNorm);
        }

        if (!chatUser && selectedChatUser?.UserDisplayPhoneNumber === toUserNorm) {
          chatUser = selectedChatUser;
        }

        const newMessage = mapSocketPayloadToMessageModel(md, toUserNorm, companyId);

        //* תגובת אימוג'י (4.11): app3 משדר reaction כ-message:new עם id של ההודעה-ההורה
        //* ו-direction=incoming. עד כאן היא עברה את כל מסלול ההודעה הנכנסת — הקפיצה את
        //* השורה, העלתה "לא נקרא" ודרסה את ההורה במטמון עם FileType=reaction (שהוסתר
        //* בטעינה הבאה). בחלון הפתוח לא נוספה בועה — בדיוק "הרשימה התעדכנה והשיחה לא".
        //* כאן: מיזוג האימוג'י ל-reactions של ההורה במטמון בלבד (כמו המאזין בצ'אט), בלי
        //* לגעת ברשימה, במונים או בשחזור איש קשר.
        if (isReactionSocketPayload(md, newMessage)) {
          const reactionTargetId = resolveReactionTargetMessageId(md, newMessage);
          const reactionEmoji = extractReactionEmojiFromPayload(md, newMessage);
          const cachedForReaction = storeState.chat.chatUsersSavedMessages.find(
            (usr) => usr.id === toUserNorm,
          );
          const cachedParent =
            reactionTargetId != null
              ? cachedForReaction?.messages.find(
                  (msg) => resolveMessageNumericId(msg) === reactionTargetId,
                )
              : undefined;
          //* רק כשההורה כבר במטמון — אחרת saveSingleMessageToUser היה יוצר רשומה חדשה
          //* שמכילה רק את התגובה, ובכניסה הבאה לשיחה החלון היה נטען ממנה (ריק).
          if (cachedParent && reactionEmoji) {
            dispatch(
              saveSingleMessageToUser({
                id: toUserNorm,
                message: {
                  ...cachedParent,
                  reactions: mergeReactionEmoji(cachedParent.reactions, reactionEmoji),
                },
              }),
            );
          }
          noteMessageOutcome(toUserNorm, reactionTargetId ?? newMessage.Id ?? null, 'reaction', 'applied-reaction');
          return;
        }

        const tempIdRaw = md.tempId ?? md.TempId;
        const hasTempId = typeof tempIdRaw === 'string' && tempIdRaw.trim().length > 0;
        const waResolved = newMessage.MessageId;

        if (payloadLooksLikeReplyMetadata(md) || newMessage.Reply != null) {
          logChatReply('socket:global', {
            keys: Object.keys(md),
            Id: newMessage.Id,
            waResolved,
            hasReply: Boolean(newMessage.Reply),
            willSkipNoId: Boolean(
              newMessage.FileType !== 'info' &&
                newMessage.Id == null &&
                !waResolved &&
                !hasTempId,
            ),
          });
        }

        // Info chips (assignee / flow / open-close) — append to open chat only.
        if (newMessage.FileType === 'info') {
          if (newMessage.Id == null) return;
          const saved = storeState.chat.chatUsersSavedMessages.find((usr) => usr.id === toUserNorm);
          const chatIsOpen = Boolean(saved) || selectedChatUser?.UserDisplayPhoneNumber === toUserNorm;
          if (chatIsOpen) {
            dispatch(saveSingleMessageToUser({ id: toUserNorm, message: newMessage }));
          }
          return;
        }

        // הערה פנימית — מופיעה בחלון השיחה הפתוח אצל כל הנציגים, אבל לא מקפיצה את
        // השיחה ברשימה ולא נוגעת בתצוגה המקדימה (LastMessage/LastMessageDate).
        // מיושר עם השרת: הליבה במכוון לא מעדכנת את ה-contact עבור הערות (Block 10b),
        // כך שההקפצה החיה כאן יצרה אי-עקביות שנעלמה בכל רענון.
        if (newMessage.FileType === 'note') {
          if (newMessage.Id == null && !hasTempId) return;
          const saved = storeState.chat.chatUsersSavedMessages.find((usr) => usr.id === toUserNorm);
          const chatIsOpen = Boolean(saved) || selectedChatUser?.UserDisplayPhoneNumber === toUserNorm;
          if (chatIsOpen) {
            dispatch(saveSingleMessageToUser({ id: toUserNorm, message: newMessage }));
          }
          noteMessageOutcome(toUserNorm, newMessage.Id ?? null, 'note', 'applied-note-no-list-bump');
          return;
        }

        //* כל יציאה מתועדת ברשומת-תוצאה במוניטור — כדי שכל ייצוא עתידי יענה
        //* חד-משמעית "האם הודעה X הגיעה ומה נעשה איתה" (לקח מחקירות 20-21/7).
        if (!chatUser) {
          //* תיקון 14.10 (הובהר 4.11): מסלול השחזור עדכן רק את הרשימה ודילג על מטמון
          //* ההודעות. אם לשיחה הזו יש כבר רשומת מטמון (נפתחה בעבר) — ההודעה נוספת אליה,
          //* כך שהכניסה הבאה לשיחה מהמטמון תכלול אותה. חשוב: זה כותב ל-cache בלבד —
          //* חלון השיחה הפתוח ניזון אך ורק ממאזין סקופ chat (singleChat), ולכן השם
          //* הקודם "-chat-applied" היה מטעה. (ההשוואה ל-selectedChatUser תמיד שקרית כאן —
          //* אילו התאימה, chatUser כבר היה נקבע למעלה.)
          const savedForRecovery = storeState.chat.chatUsersSavedMessages.find(
            (usr) => usr.id === toUserNorm,
          );
          const cacheExistsDuringRecovery = Boolean(savedForRecovery);
          if (cacheExistsDuringRecovery && (newMessage.Id != null || hasTempId)) {
            dispatch(saveSingleMessageToUser({ id: toUserNorm, message: newMessage }));
          }
          noteMessageOutcome(
            toUserNorm,
            newMessage.Id ?? null,
            newMessage.Type ?? '?',
            cacheExistsDuringRecovery ? 'contact-missing-recovery-cache-applied' : 'contact-missing-recovery',
          );
          void recoverUnknownChatUser(toUserNorm);
          return;
        }

        if (isForeignChatUserCompany(chatUser, companyId)) {
          noteMessageOutcome(toUserNorm, newMessage.Id ?? null, newMessage.Type ?? '?', 'dropped-foreign-company');
          return;
        }

        if (!chatUser.CompanyID) {
          chatUser = repairChatUserCompanyIdIfEmpty(chatUser, companyId);
          updateSingleChatUserByPhone(toUserNorm, { CompanyID: companyId });
          recordSocketHealth('sync', 'company-tag-repaired', 'info', { phone: toUserNorm });
        }

        if (newMessage.Id == null && !waResolved && !hasTempId) {
          noteMessageOutcome(toUserNorm, null, newMessage.Type ?? '?', 'skipped-no-id');
          return;
        }

        // עדכון chatUser עם המידע החדש
        const isIncoming = newMessage.Type === 'incoming';
        // The server includes the post-transition flowStatusCode in the SignalR
        // payload (see WhatsappController.processMessageSideEffects /
        // SendOutgoingMessage). If present, it is the authoritative value and
        // must propagate into the cached chat user so that the chat header,
        // sidebar counters, and view filters all stay in sync without a
        // manual refetch.
        const flowStatusCodeRaw = (md.flowStatusCode ?? md.FlowStatusCode) as unknown;
        const incomingFlowStatusCode =
          typeof flowStatusCodeRaw === 'number' && flowStatusCodeRaw >= 1 && flowStatusCodeRaw <= 6
            ? (flowStatusCodeRaw as ChatUserModel['flowStatusCode'])
            : undefined;
        const managerIdRaw = (md.managerId ?? md.ManagerId) as unknown;
        // message:new is not an unassign event. A missing/null managerId means
        // "no assignee info in this payload" — keep the displayed assignee.
        // Real unassign arrives on session:update.
        const normalizedManagerId =
          'managerId' in md || 'ManagerId' in md
            ? normalizeSocketManagerId(managerIdRaw)
            : null;
        const managerPatch: Partial<ChatUserModel> = normalizedManagerId
          ? buildAssigneePatchFromManagerId(normalizedManagerId)
          : {};
        const sessionPatch: Partial<ChatUserModel> =
          sessionIdRaw && typeof sessionIdRaw === 'string'
            ? { SessionId: sessionIdRaw, IsOpen: true }
            : {};
        const newChatUser: ChatUserModel = {
          ...chatUser,
          ...sessionPatch,
          ...managerPatch,
          LastMessage: newMessage.Message || chatUser.LastMessage,
          LastMessageDate: newMessage.DateCreate || chatUser.LastMessageDate,
          LastMessageType: newMessage.Type || chatUser.LastMessageType,
          LastMessageMediaType: newMessage.FileType || chatUser.LastMessageMediaType,
          ...(incomingFlowStatusCode !== undefined
            ? { flowStatusCode: incomingFlowStatusCode }
            : {}),
          ...(isIncoming
            ? (() => {
                const waWindowRaw = md.isWhatsappWindowOpen ?? md.IsWhatsappWindowOpen;
                const isRestricted24: boolean =
                  typeof waWindowRaw === 'boolean' ? waWindowRaw : true;
                return {
                  IncomingLastMessage: newMessage.Message || null,
                  IncomingLastMessageDate: newMessage.DateCreate || null,
                  incomingLastMessageId: newMessage.Id || null,
                  UnreadMessagesNumber: (chatUser.UnreadMessagesNumber ?? 0) + 1,
                  UnreadNumber: (chatUser.UnreadNumber ?? 0) + 1,
                  // isRestricted24 maps to isWhatsappWindowOpen — an inbound message reopens the 24h window.
                  isRestricted24,
                };
              })()
            : {}),
          ...(newMessage.Type === 'outcoming'
            ? {
                OutcomingLastMessage: newMessage.Message || null,
                OutcomingLastMessageDate: newMessage.DateCreate || null,
                OutcomingLastMessageId: newMessage.Id || null,
                OutcomingLastMessageStatus: newMessage.Status || null,
              }
            : {}),
        };

        // עדכון ה-views
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveMessageUpdateChatUserInView(key, newChatUser, newMessage);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );


        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            mySnoozedChatUsers: newChatUsers.mySnoozedChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
            completedChatUsers: newChatUsers.completedChatUsers,
          }),
        );

        // עדכון selectedChatUser אם זה המשתמש הנבחר
        if (selectedChatUser?.UserDisplayPhoneNumber === newChatUser.UserDisplayPhoneNumber) {
          dispatch(setSelectedChatUser(newChatUser));
        }

        // עדכון הודעות שמורות
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === toUserNorm,
        );
        if (thisUserSavedMessages) {
          dispatch(saveSingleMessageToUser({ id: toUserNorm, message: newMessage }));
        }

        getUnreadChatUsers();
        // company-wide event ⇒ רענון מוני התצוגות (כולל team/all/unassigned)
        scheduleUnreadCountsRefresh('message:new');

        noteMessageOutcome(toUserNorm, newMessage.Id ?? null, newMessage.Type ?? '?', 'applied');
      } catch (error: any) {
        console.log('onSocketReceiveMessage error', error);
      }
    },
    [receiveMessageUpdateChatUserInView, recoverUnknownChatUser],
  );
  //* Update message
  const onSocketUpdateMessage = React.useCallback((messageObject: UpdateMessageSocketType | SocketMessageData) => {
    try {
      let recipientId: string;
      let messageId: string;
      let newStatus: string;
      let tempId: string | undefined;

      if ('entry' in messageObject && (messageObject as UpdateMessageSocketType).entry) {
        const value = (messageObject as UpdateMessageSocketType).entry[0].changes[0].value;
        const statuses = value?.statuses?.[0];
        if (!statuses) return;
        recipientId = statuses.recipient_id;
        messageId = statuses.id;
        newStatus = statuses.status;
      } else if ('toUser' in messageObject) {
        const flat = messageObject as SocketMessageData;
        recipientId = flat.toUser;
        messageId = flat.whatsappMessageId;
        newStatus = flat.status;
        tempId = (flat as any).tempId;
      } else {
        return;
      }

      const storeState = store.getState();
      const { chatUsersSavedMessages } = storeState.chat;

      const thisUserSavedMessage = chatUsersSavedMessages.find((usr) => usr.id === recipientId);
      if (!thisUserSavedMessage) return;

      let existingMessage = thisUserSavedMessage.messages.find(
        (msg) =>
          msg.messageId === messageId ||
          msg.MessageId === messageId ||
          (tempId && (msg.TempId === tempId || msg.tempId === tempId)),
      );
      if (!existingMessage) return;

      existingMessage = {
        ...existingMessage,
        MessageId: messageId,
        messageId: messageId,
        Status: getMessageStatus(newStatus ?? '', existingMessage.Status ?? ''),
      };
      dispatch(saveSingleMessageToUser({ id: recipientId, message: existingMessage }));
    } catch (error) {
      console.warn(error);
    }
  }, []);
  //* ReceiveTemplateMessage
  const receiveTemplateMessageUpdateChatUserInView = React.useCallback(
    (view: ChatViewUsersId, messageObject: ReceiveTemplateMessageSocketType) => {
      const storeState = store.getState();
      const areUsersRequested = storeState.chat.areUsersRequested;
      
      if (!areUsersRequested[`${view}`]) {
        console.log(`[DEBUG Socket Template] ${view} - returning [] because areUsersRequested is false`);
        return [];
      }

      const chatUsers = storeState.chat.chatUsers[`${view}`];

      let targetUser = chatUsers.find((user) => user.UserDisplayPhoneNumber === messageObject.phone);
      if (!targetUser) {
        console.log(`[DEBUG Socket Template] ${view} - targetUser not found, returning chatUsers as-is`, chatUsers.length);
        return chatUsers;
      }


      targetUser = {
        ...targetUser,
        LastMessage: messageObject.displayMessage,
        LastMessageDate: messageObject.date,
        //? LastMessageMediaType: 'template',
        LastMessageType: 'outcoming',
        OutcomingLastMessage: messageObject.displayMessage,
        OutcomingLastMessageStatus: messageObject.Status,
      };

      // FIX: Use chatUsers (current view) instead of allChatUsers
      let newUsersList = chatUsers.filter(
        (user) => user.UserDisplayPhoneNumber !== messageObject.phone,
      );
      
      
      newUsersList = insertUsersAccordingToFilters(targetUser, newUsersList, view);

      return newUsersList;
    },
    [insertUsersAccordingToFilters],
  );
  const onSocketReceiveTemplateMessage = React.useCallback(
    (messageObject: ReceiveTemplateMessageSocketType) => {
      try {
        const storeState = store.getState();
        const { chatUsers } = storeState.chat;

        

        //* Fill & update views with new chat user
        const newChatUsers: typeof chatUsers = (
          Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        ).reduce(
          (acc, key) => {
            if (key === 'loading') {
              acc[key] = chatUsers[key];
            } else {
              acc[key] = receiveTemplateMessageUpdateChatUserInView(key, messageObject);
            }

            return acc;
          },
          {} as typeof chatUsers,
        );

        
        dispatch(
          setChatUsers({
            allChatUsers: newChatUsers.allChatUsers,
            myChatUsers: newChatUsers.myChatUsers,
            mySnoozedChatUsers: newChatUsers.mySnoozedChatUsers,
            notMyChatUsers: newChatUsers.notMyChatUsers,
            unassignedChatUsers: newChatUsers.unassignedChatUsers,
            botsChatUsers: newChatUsers.botsChatUsers,
            customChatUsers: newChatUsers.customChatUsers,
            completedChatUsers: newChatUsers.completedChatUsers,
          }),
        );

        //* Update selected chat user if it is his number
        if (storeState.chat.selectedChatUser?.UserDisplayPhoneNumber === messageObject.phone) {
          dispatch(
            setSelectedChatUser({
              ...storeState.chat.selectedChatUser,
              LastMessage: messageObject.displayMessage,
              LastMessageDate: messageObject.date,
              //? LastMessageMediaType: 'template',
              LastMessageType: 'outcoming',
              OutcomingLastMessage: messageObject.displayMessage,
              OutcomingLastMessageStatus: messageObject.Status,
            }),
          );
        }

        //* update saved messages for this user if exists
        const thisUserSavedMessages = storeState.chat.chatUsersSavedMessages.find(
          (usr) => usr.id === messageObject.phone,
        );
        if (!thisUserSavedMessages) return;

        let currentMessage = thisUserSavedMessages.messages.find(
          (msg) =>
            (msg.TempId && msg.TempId === messageObject.tempId) ||
            (msg.tempId && msg.tempId === messageObject.tempId),
        );
        if (!currentMessage) return;
        currentMessage = {
          ...currentMessage,
          MessageId: messageObject.messageId,
          Status: messageObject.Status,
          StatusHeb: messageObject.StatusHeb,
        };

        dispatch(saveSingleMessageToUser({ id: messageObject.phone, message: currentMessage }));
      } catch (error) {
        console.warn('onSocketReceiveTemplateMessage error: ', error);
      }
    },

    [receiveTemplateMessageUpdateChatUserInView],
  );
  //? Unread Notification

  //* Unread Update
  const onSocketUpdateChatUserUnread = React.useCallback(async function (
    messageObject: UpdateChatUserUnreadSocketType,
  ) {
    try {
      if (messageObject.source === 'setReadAPI') {
        updateSingleChatUser(messageObject.id, {
          UnreadMessagesNumber: messageObject.unreadNumber,
          UnreadNumber: messageObject.unreadNumber,
        });

        const storeState = store.getState();
        getUnreadChatUsers();
        // company-wide event ⇒ רענון מוני התצוגות (כולל team/all/unassigned)
        scheduleUnreadCountsRefresh('updateChatUserUnread');

        if (storeState.chat.chatUsersFilters.isOnlyUnread && messageObject.unreadNumber === 0) {
          const updatedChatUsers: typeof storeState.chat.chatUsers = (
            Object.keys(storeState.chat.chatUsers) as Array<keyof typeof storeState.chat.chatUsers>
          ).reduce(
            (acc, key) => {
              if (key === 'loading') {
                acc[key] = storeState.chat.chatUsers[key];
              } else {
                acc[key] = storeState.chat.chatUsers[key].filter(
                  (user) => user.Id !== messageObject.id,
                );
              }
              return acc;
            },
            {} as typeof storeState.chat.chatUsers,
          );

          dispatch(
            setChatUsers({
              allChatUsers: updatedChatUsers.allChatUsers,
              myChatUsers: updatedChatUsers.myChatUsers,
              mySnoozedChatUsers: updatedChatUsers.mySnoozedChatUsers,
              notMyChatUsers: updatedChatUsers.notMyChatUsers,
              unassignedChatUsers: updatedChatUsers.unassignedChatUsers,
              botsChatUsers: updatedChatUsers.botsChatUsers,
              customChatUsers: updatedChatUsers.customChatUsers,
              completedChatUsers: updatedChatUsers.completedChatUsers,
            }),
          );
        }
      }
    } catch (error) {
      console.warn('onSocketUpdateChatUserUnread error: ', error);
    }
  }, []);
  //TODO Extension browser notifications
  //* Notifications
  const onSocketNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const enableSystemNotifications = storeState.userConfig.enableSystemNotifications ?? true;
        const linkToObjectNum = storeState.company?.LinkToObjectNum;

        if (enableSystemNotifications) {
          const phone = messageObject.displayPhoneNumber;
          const currentUser = storeState.chat.selectedChatUser;
          if (phone === currentUser?.UserDisplayPhoneNumber) return;

          const resolvedFs =
            findChatUserByPhoneInStore(phone)?.flowStatusCode ?? null;
          if (
            !shouldDeliverGeneralNotification(
              storeState.userConfig.notificationPreferences,
              messageObject,
              resolvedFs,
            )
          ) {
            return;
          }

          const targetLink =
            storeState.company?.CompanyType === 'fireberry' &&
            (messageObject.fireberryAccountLink
              ? messageObject.fireberryAccountLink
              : `https://app.powerlink.co.il/app/views/${
                  linkToObjectNum && linkToObjectNum === 'second' ? '2' : '1'
                }?q=${phone}`);

          if (!isNative && getBrowserNotifStatus() === 'granted') {
            //? handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{phone}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
                // @ts-ignore
                iconVariant: storeState.company?.CompanyType === 'fireberry' && {
                  info: <NotificationIcon type={'info'} data={{ link: targetLink }} />,
                },
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  const onSocketTaggedUsersNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const storeState = store.getState();
        const user = storeState.user;
        if (user?.Udid !== messageObject.notifyContactUserglobalid) return;

        //* הפעמון מתעדכן דרך 'update:new' (הגשר מהנוד) — לא כאן, כדי שלא ייווצרו
        //* שתי כניסות אם שני האירועים מגיעים. האירוע הזה נשאר ל-toast בלבד.

        if (!shouldDeliverTaggedNoteNotification(storeState.userConfig.notificationPreferences)) {
          return;
        }

        const enableSystemNotifications = storeState.userConfig.enableSystemNotifications ?? true;
        if (enableSystemNotifications) {
          const phone = messageObject.displayPhoneNumber;
          // const targetUser = chatUsersGlobalRef.current.notMyChats.find(
          //   (user) => user.UserDisplayPhoneNumber === phone,
          // );

          if (!isNative && getBrowserNotifStatus() === 'granted') {
            //TODO handleBrowserNotification(`Funner - ${targetUser.FullName}`, messageObject.notificationText);
            handleBrowserNotification(`Funner`, messageObject.notificationText);
          } else {
            const currentView = getCurrentView();
            enqueueSnackbar(
              <Grid container spacing={0.5}>
                <Grid size={12}>
                  <Box
                    sx={{ color: 'info.contrastText' }}
                    component={Link}
                    href={currentView.path + '/' + phone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate(currentView.path + '/' + phone);
                    }}
                  >
                    <Grid container spacing={1}>
                      {/* //TODO <Grid>{targetUser?.FullName || ''}</Grid> */}
                      <Grid>{messageObject.displayPhoneNumber}</Grid>
                    </Grid>
                  </Box>
                </Grid>
                <Grid size={12}>{messageObject.notificationText}</Grid>
              </Grid>,
              {
                variant: 'info',
              },
            );
          }
        }
      } catch (error) {
        console.warn('onSocketTaggedUsersNotification error: ', error);
      }
    },
    [enqueueSnackbar, navigate],
  );
  //* עדכון חדש בטבלת Updates (תיוג וכו') — נדחף מהנוד דרך גשר הסוקט ב-C#
  //* לקבוצת המשתמש היעד. מזין את הפעמון + ה-badge חי, בלי רענון.
  const onSocketUpdateNew = React.useCallback((messageObject: any) => {
    try {
      const storeState = store.getState();
      const myUdid = storeState.user?.Udid;
      const targetUserId = messageObject?.targetUserId;
      //* השידור הוא לקבוצת המשתמש, אבל שומרים guard — הגנה מפני שינוי עתידי בצד השרת
      if (!myUdid || !targetUserId || myUdid.toLowerCase() !== String(targetUserId).toLowerCase()) {
        return;
      }
      const createdAtMs = Date.parse(messageObject?.createdAt ?? '') || Date.now();
      dispatch(
        pushSystemNotification({
          //* id של שורת השרת + fromServer — כך ה-hydrate הבא מחליף ולא מכפיל
          id: `srv-${messageObject?.id}`,
          fromServer: true,
          message: String(messageObject?.body ?? ''),
          createdAt: createdAtMs,
          visual: messageObject?.type === 'mention' ? { kind: 'tag' } : { kind: 'note' },
          link: messageObject?.link ?? null,
        }),
      );
    } catch (error) {
      console.warn('onSocketUpdateNew error: ', error);
    }
  }, []);

  const onSocketFunnerServiceNotification = React.useCallback(
    (messageObject: any) => {
      try {
        const { notificationText, type, durationSeconds } = messageObject;
        const scheme = typeof type === 'string' && type.includes('/') ? type.split('/') : ['#2196f3', '#ffffff'];
        const bg = (scheme[0] ?? '#2196f3').trim();
        const fg = (scheme[1] ?? '#ffffff').trim();
        dispatch(
          pushSystemNotification({
            message: String(notificationText ?? ''),
            backgroundColor: bg,
            textColor: fg,
          }),
        );
        enqueueSnackbar(notificationText, {
          autoHideDuration: (durationSeconds ?? 5) * 1000,
          variant: 'info',
          className: 'funner-service-notification',
          style: {
            ['--funner-service-bg' as string]: bg,
            ['--funner-service-fg' as string]: fg,
          } as React.CSSProperties,
        });
      } catch (error) {
        console.warn('onSocketFunnerServiceNotification error: ', error);
      }
    },
    [dispatch, enqueueSnackbar],
  );

  const onSocketUnreadNotification = React.useCallback(() => {
    void getUnreadChatUsers();
    if (store.getState().userConfig.enableSystemNotifications ?? true) {
      enqueueSnackbar('יש לך הודעות חדשות שממתינות לקריאה', { variant: 'info' });
    }
  }, [enqueueSnackbar]);

  //* Chat user updates
  const onSocketLabelUpdate = React.useCallback(function (messageObject: LabelUpdateSocketType) {
    try {
      updateSingleChatUserByPhone(messageObject.phoneId, { LableName: messageObject.label });
      // Label update doesn't affect unread counts, no need to refresh
    } catch (error) {
      console.log('onSocketLabelUpdate error: ', error);
    }
  }, []);
  const onSocketConversationOpenStatus = React.useCallback(
    (messageObject: ConversationOpenStatusSocketType) => {
      try {
        const { chatUserPhoneId, isOpen } = messageObject;
        updateSingleChatUserByPhone(chatUserPhoneId, { IsOpen: isOpen });
        // Conversation open/close status doesn't affect unread counts, no need to refresh

        //? const storeState = store.getState();
        // const { chatUsers } = storeState.chat;

        // const { open } = storeState.chat.chatUsersFilters;
        // const doesUserMatchFilters =
        //   open === 'open' ? isOpen === true : open === 'close' ? isOpen === false : true;

        // const newChatUsers: typeof chatUsers = (
        //   Object.keys(chatUsers) as Array<keyof typeof chatUsers>
        // ).reduce(
        //   (acc, key) => {
        //     if (key === 'loading') {
        //       acc[key] = chatUsers[key];
        //     } else {
        //       if (!doesUserMatchFilters) {
        //         acc[key] = chatUsers[key].filter((user) => user.UserDisplayPhoneNumber !== chatUserPhoneId);
        //       }
        //     }

        //     return acc;
        //   },
        //   {} as typeof chatUsers,
        // );
      } catch (error) {
        console.warn('onSocketConversationOpenStatus error: ', error);
      }
    },
    [],
  );
  const onSocketUpdateChatUserDontDisturb = React.useCallback(
    (messageObject: UpdateChatUserDontDisturbSocketType) => {
      try {
        const dontDisturb = messageObject.chatUser.DontDisturb;
        updateSingleChatUser(messageObject.chatUser.Id, { DontDisturb: dontDisturb });
        // DontDisturb status doesn't affect unread counts, no need to refresh
      } catch (error) {
        console.warn('onSocketUpdateChatUserDontDisturb error: ', error);
      }
    },
    [],
  );
  const onSocketContactBotStatusChange = React.useCallback((messageObject: any) => {
    try {
      const { UserId, isBot } = messageObject;
      updateSingleChatUserByPhone(UserId, { isBot });
      // כניסת/יציאת בוט מזיזה את השיחה בין המונים הרגילים למונה תצוגת «בוטים»
      scheduleUnreadCountsRefresh('bot-status-change');
    } catch (error) {
      console.log('onSocketUpdateFunnerUserActive error: ', error);
    }
  }, []);

  //* Update app users
  const onSocketUpdateFunnerUserActive = React.useCallback(
    (messageObject: any) => {
      try {
        const { funnerUserId, isActive } = messageObject;
        console.log('[DEBUG-REFRESH] updateFunnerUserActive received', new Date().toISOString(), { funnerUserId, isActive });
        const user = store.getState().user;
        if (funnerUserId === user?.Udid) return logout();

        dispatch(
          updateSingleApplicationUser({
            userId: funnerUserId,
            params: {
              IsActive: isActive,
            },
          }),
        );
      } catch (error) {
        console.warn('onSocketUpdateFunnerUserActive error: ', error);
      }
    },
    [logout],
  );
  //* Company platforms update
  const onSocketCompanyPlatformsChanged = React.useCallback(
    (messageObject: UpdateCompanyPlatformsSocketType) => {
      try {
        const { ChangeNiceTextObject, EditorName, NewObjectJson } = messageObject;

        const parsedPlatforms: CompanyPlatformsModel = JSON.parse(NewObjectJson);
        dispatch(updateCompanyPlatformsData(parsedPlatforms));

        // Don't show notification if current user made the change
        const currentUser = store.getState().user;
        const isCurrentUser = currentUser?.FullName === EditorName || currentUser?.FirstName === EditorName;

        if (!isCurrentUser) {
          enqueueSnackbar(ChangeNiceTextObject + ' by ' + EditorName, {
            variant: 'info',
          });
        }
      } catch (error) {
        console.warn('onSocketCompanyPlatformsChanged error: ', error);
      }
    },
    [],
  );

  //* Manual session update (flow / assignee) from .NET SessionController PUT
  const onSocketSessionUpdate = React.useCallback((messageObject: SessionUpdateSocketType) => {
    try {
      const phone = messageObject?.toUser;
      if (!phone) return;

      const updates: Partial<ChatUserModel> = {};
      const flowRaw = messageObject.flowStatusCode;
      if (typeof flowRaw === 'number' && flowRaw >= 1 && flowRaw <= 6) {
        updates.flowStatusCode = flowRaw as ChatUserModel['flowStatusCode'];
        if (typeof messageObject.flowStatus === 'string') {
          updates.flowStatus = messageObject.flowStatus;
        }
      }
      if (messageObject.status === 'open' || messageObject.status === 'closed') {
        updates.IsOpen = messageObject.status === 'open';
      }
      if (typeof messageObject.sessionId === 'string' && messageObject.sessionId.trim()) {
        updates.SessionId = messageObject.sessionId;
      }
      if ('managerId' in messageObject) {
        const flowRaw = messageObject.flowStatusCode;
        const mid = normalizeSocketManagerId(messageObject.managerId);
        const completingWithoutUnassign = flowRaw === 6 && mid === null;
        if (!completingWithoutUnassign) {
          Object.assign(updates, buildAssigneePatchFromManagerId(mid));
        }
      }
      //* מנרמל מועד תזכורת ל-ISO ומתמיד אותו (שורד רענון רשימה גם כשה-fetch לא מחזיר snoozeEndTime).
      if ('snoozeEndTime' in messageObject) {
        updates.snoozeEndTime = normalizeSnoozeEndTimeFromApi(messageObject.snoozeEndTime);
      } else if (typeof flowRaw === 'number' && flowRaw !== 5) {
        updates.snoozeEndTime = null;
      }
      //* לוכדים את השורה לפני העדכון-במקום: updateSingleChatUserByPhone מעדכן את
      //* ה-store סינכרונית, ולכן השוואת "האם ההשתייכות השתנתה" שנעשתה אחריו
      //* השוותה ערך חדש לעצמו ויצאה תמיד שלילית — השיחה נשארה בתצוגה הלא-נכונה
      //* (למשל "לא משויכות" אחרי שיוך) עד רענון, אצל כל הנציגים (תוקן 26.10).
      const preUpdateRow = findChatUserByPhoneInStore(phone);

      if (Object.keys(updates).length > 0) {
        updateSingleChatUserByPhone(phone, updates);
        if (updates.snoozeEndTime) {
          setPersistedSnoozeUntil(phone, updates.snoozeEndTime);
        } else if (updates.snoozeEndTime === null) {
          setPersistedSnoozeUntil(phone, null);
        }
      }

      //* כל שינוי שמשפיע על השתייכות לתצוגות — שיוך, סטטוס פלואו או פתיחה/סגירה —
      //* בונה מחדש את רשימות התצוגות באותו מנגנון של הודעה נכנסת: הסרה והכנסה לפי
      //* הפילטרים של כל תצוגה (עד 26.10 זה רץ על שינוי שיוך בלבד, וגם אז לא בפועל).
      const assigneeChanged =
        'NotificationContactUser' in updates &&
        (preUpdateRow?.NotificationContactUser || null) !== (updates.NotificationContactUser || null);
      const flowChanged =
        updates.flowStatusCode !== undefined && preUpdateRow?.flowStatusCode !== updates.flowStatusCode;
      const openChanged = updates.IsOpen !== undefined && preUpdateRow?.IsOpen !== updates.IsOpen;

      if (preUpdateRow && (assigneeChanged || flowChanged || openChanged)) {
        relocateChatUserInViewLists(phone);
        //* מוני התצוגות (שלי / לא משויכות / בהקפאה וכו') משתנים עם ההשתייכות
        scheduleUnreadCountsRefresh('session:update-membership');
      }

    } catch (error) {
      console.warn('onSocketSessionUpdate error: ', error);
    }
  }, []);

  //* עדכון חי של תגיות שיחה (SessionConversationTag) מהנוד — מעדכן את שורת
  //* הרשימה לפי הטלפון (toUser); selectedChatUser מתעדכן באותו reducer, כך
  //* שגם הצ'יפים בכותרת הצ'ט הפתוח מתרעננים.
  const onSocketConversationTags = React.useCallback(
    (messageObject: ConversationTagsSocketType) => {
      try {
        const phone = messageObject?.toUser;
        if (!phone || !Array.isArray(messageObject?.tags)) return;
        const preUpdateRow = findChatUserByPhoneInStore(phone);
        updateSingleChatUserByPhone(phone, { ConversationTags: messageObject.tags });
        //* תגית שהתווספה/הוסרה משנה השתייכות לתצוגות מסוננות-תגית (וגם לתצוגות
        //* מותאמות עם פילטר תגית) — בנייה-מחדש כמו בשיוך/סטטוס, לא רק עדכון-במקום.
        const tagsChanged =
          preUpdateRow &&
          JSON.stringify([...(preUpdateRow.ConversationTags ?? [])].sort()) !==
            JSON.stringify([...messageObject.tags].sort());
        if (preUpdateRow && tagsChanged) {
          relocateChatUserInViewLists(phone);
        }
      } catch (error) {
        console.warn('onSocketConversationTags error: ', error);
      }
    },
    [],
  );

  //* אדמין שינה לי תצוגות (/users/views) או הרשאות (/users/permissions) —
  //* האירוע ממוקד אליי (group לפי Udid), ולכן מספיק לרענן את קונפיג המשתמש;
  //* userViewsConfig וגם modulePermissions נטענים מאותה תשובה, והסיידבר,
  //* התפריטים וה-route guards מגיבים מיידית.
  const onSocketUserConfigChanged = React.useCallback(() => {
    try {
      void getUserConfig();
    } catch (error) {
      console.warn('onSocketUserConfigChanged error: ', error);
    }
  }, []);

  const onSocketNotif = React.useCallback(
    (type: SocketEventType, message: string | null | any) => {
      // אם ההודעה היא כבר אובייקט, נשתמש בה ישירות, אחרת ננסה לפרסר אותה
      const messageObject =
        typeof message === 'object' && message !== null ? message : safeParseJSON(message);
      if (!messageObject) return;
      switch (type) {
        case 'ReceiveMessage':
        case 'message:new':
          onSocketReceiveMessage(messageObject);
          break;
        case 'session:update':
          onSocketSessionUpdate(messageObject);
          break;
        case 'conversation:tags':
          onSocketConversationTags(messageObject);
          break;
        case 'MessageStatusUpdate':
        case 'status:update':
          onSocketUpdateMessage(messageObject);
          break;
        case 'ReceiveTemplateMessage':
          onSocketReceiveTemplateMessage(messageObject);
          break;
        case 'unreadNotification':
          onSocketUnreadNotification();
          break;
        case 'UnreadUpdate':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'updateChatUserUnread':
          onSocketUpdateChatUserUnread(messageObject);
          break;
        case 'Notification':
          onSocketNotification(messageObject);
          break;
        case 'TaggedUsersNotification':
          onSocketTaggedUsersNotification(messageObject);
          break;
        case 'update:new':
          onSocketUpdateNew(messageObject);
          break;
        case 'userConfig:views':
        case 'userConfig:permissions':
          onSocketUserConfigChanged();
          break;
        case 'funnerServiceNotification':
          onSocketFunnerServiceNotification(messageObject);
          break;
        case 'labelUpdate':
          onSocketLabelUpdate(messageObject);
          break;
        case 'conversationOpenStatus':
          onSocketConversationOpenStatus(messageObject);
          break;
        case 'UpdateChatUserDontDisturb':
          onSocketUpdateChatUserDontDisturb(messageObject);
          break;
        case 'ContactBotStatusChange':
          onSocketContactBotStatusChange(messageObject);
          break;
        case 'updateFunnerUserActive':
          onSocketUpdateFunnerUserActive(messageObject);
          break;
        case 'UpdateCompanyPlatforms':
          onSocketCompanyPlatformsChanged(messageObject);
          break;
        case 'Logout':
          console.log('[DEBUG-REFRESH] Socket Logout event received', new Date().toISOString());
          logout();
          break;
        default:
          break;
      }

      //TODO if (applicationType === 'extension') {
      //   const sendContent = {
      //     messageObject: messageObject,
      //   };

      //   sendMessageToBackground('socketReceiveMessage', sendContent);
      // }
    },
    [
      logout,
      onSocketCompanyPlatformsChanged,
      onSocketContactBotStatusChange,
      onSocketConversationOpenStatus,
      onSocketFunnerServiceNotification,
      onSocketLabelUpdate,
      onSocketNotification,
      onSocketReceiveMessage,
      onSocketSessionUpdate,
      onSocketConversationTags,
      onSocketReceiveTemplateMessage,
      onSocketTaggedUsersNotification,
      onSocketUnreadNotification,
      onSocketUpdateChatUserDontDisturb,
      onSocketUpdateChatUserUnread,
      onSocketUpdateFunnerUserActive,
      onSocketUpdateMessage,
      onSocketUpdateNew,
      onSocketUserConfigChanged,
    ],
  );

  //* ===== FIX #2 (2026-07-13) — gated registration (replaces early-return) =====
  //* Only subscribe to the real socket events once auth is ready; otherwise pass
  //* the stable empty array so no handlers are registered. This preserves the
  //* original "don't listen until signed in" behavior while keeping hook order
  //* constant across renders.
  const activeSocketEvents = isInitialized && isLoggedIn ? socketEvents : NO_SOCKET_EVENTS;

  //* ניטור בלבד — תיעוד מעברי השערה (auth gating) של המאזינים הגלובליים,
  //* כדי שביומן ניטור הסוקט יהיה ברור מתי ההאזנה הושבתה/הופעלה ולמה.
  const isGloballyGated = activeSocketEvents === NO_SOCKET_EVENTS;
  React.useEffect(() => {
    //* השבתה לפני שהאימות אותחל = עליית אפליקציה רגילה (info);
    //* השבתה אחרי אתחול = ניתוק האזנה אמיתי באמצע סשן (warning).
    recordSocketHealth(
      'listeners',
      isGloballyGated ? 'global-gated-off' : 'global-gated-on',
      isGloballyGated && isInitialized ? 'warning' : 'info',
      { isInitialized, isLoggedIn },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isGloballyGated]);

  useSocketListeners(activeSocketEvents, onSocketNotif, 'global');
}
